/**
 * Read-only helper for summary / detail pages that display a stored note field.
 *
 * Detects plain vs structured JSON automatically so callers never render raw JSON.
 * Does not include an "Edit note" action — use {@link StructuredNoteField} for editing.
 */

import React from 'react';
import { Typography } from 'antd';
import { parseNoteValue } from './serialization';
import { StructuredNoteDisplay } from './StructuredNoteDisplay';

const { Paragraph } = Typography;

interface StructuredNoteContentProps {
  /** Stored note value (plain string or structured JSON). */
  value: string | null | undefined;
  /** Optional italic placeholder when the note is empty. */
  emptyText?: string;
}

/**
 * Renders a note for read-only contexts (application summary, appointment popup, etc.).
 */
export function StructuredNoteContent({ value, emptyText }: StructuredNoteContentProps) {
  const parsed = parseNoteValue(value);

  if (parsed.mode === 'structured') {
    return <StructuredNoteDisplay structuredNote={parsed.payload.structuredNote} />;
  }

  const content = parsed.content.trim();
  if (!content) {
    return emptyText ? (
      <Typography.Text type="secondary" italic>
        {emptyText}
      </Typography.Text>
    ) : null;
  }

  return <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{content}</Paragraph>;
}
